/**
 * Notifications & subscriptions API client.
 *
 * Built on top of `lib/api.ts`. All endpoints under `/api/preferences` and
 * `/api/subscriptions` require an authenticated principal (Easy Auth populates
 * `X-MS-CLIENT-PRINCIPAL` only when a valid bearer token reaches the
 * Function App), which the request layer attaches automatically.
 */
import { api } from './api';
import type {
  Subscription,
  SubscriptionFilters,
  SubscriptionCadence,
  NotificationChannel,
  UserPreferences,
  SubscriptionDelivery,
  Recipient
} from '../types';

export interface CreateSubscriptionInput {
  name: string;
  cadence: SubscriptionCadence;
  channels: NotificationChannel[];
  filters: SubscriptionFilters;
  reminderDayBefore?: boolean;
  /** Reminder offsets in hours (1–336). Supersedes `reminderDayBefore` when both are sent. */
  reminderOffsets?: number[];
  sendCalendarInviteOnMatch?: boolean;
}

export type UpdateSubscriptionInput = Partial<CreateSubscriptionInput> & { isActive?: boolean };

export interface CreateContactInput {
  email: string;
  phone?: string;
  displayName?: string;
  timezone?: string;
  /** When true, the admin vouches for the phone (skip the OTP round-trip). */
  adminVerifiedPhone?: boolean;
}

export type UpdateContactInput = Partial<CreateContactInput>;

export const notificationsApi = {
  // Subscriptions
  list: () => api.get<Subscription[]>('/api/subscriptions'),
  get: (id: string) => api.get<Subscription>(`/api/subscriptions/${encodeURIComponent(id)}`),
  create: (input: CreateSubscriptionInput) => api.post<Subscription>('/api/subscriptions', input),
  update: (id: string, patch: UpdateSubscriptionInput) =>
    api.patch<Subscription>(`/api/subscriptions/${encodeURIComponent(id)}`, patch),
  remove: (id: string, hard = false) =>
    api.del<void>(`/api/subscriptions/${encodeURIComponent(id)}${hard ? '?hard=1' : ''}`),
  test: (id: string) => api.post<{ ok: boolean }>(`/api/subscriptions/${encodeURIComponent(id)}/test`),
  deliveries: (id: string, limit = 50) =>
    api.get<SubscriptionDelivery[]>(`/api/subscriptions/${encodeURIComponent(id)}/deliveries?limit=${limit}`),

  // Preferences
  getPreferences: () => api.get<UserPreferences>('/api/preferences'),
  updatePreferences: (patch: Partial<UserPreferences>) =>
    api.put<UserPreferences>('/api/preferences', patch),
  testWeeklyDigest: () => api.post<{ ok: boolean; testId: string }>('/api/notifications/weekly-digest/test'),

  // Phone verification
  startPhoneVerify: (phone: string) =>
    api.post<{ ok: boolean }>('/api/preferences/phone/verify-start', { phone }),
  confirmPhoneVerify: (phone: string, code: string) =>
    api.post<UserPreferences>('/api/preferences/phone/verify-confirm', { phone, code }),
  deletePhone: () => api.del<void>('/api/preferences/phone')
};

/**
 * Admin "subscribe others" API — write-on-behalf recipient + subscription
 * management. All endpoints require the platform-admin role (server enforces
 * `requireAdmin`).
 */
export const adminRecipientsApi = {
  list: () => api.get<Recipient[]>('/api/mgmt/recipients'),
  createContact: (input: CreateContactInput) => api.post<Recipient>('/api/mgmt/recipients', input),
  updateContact: (id: string, patch: UpdateContactInput) =>
    api.patch<Recipient>(`/api/mgmt/recipients/${encodeURIComponent(id)}`, patch),
  deleteContact: (id: string) => api.del<void>(`/api/mgmt/recipients/${encodeURIComponent(id)}`),

  listSubs: (id: string) =>
    api.get<Subscription[]>(`/api/mgmt/recipients/${encodeURIComponent(id)}/subscriptions`),
  createSub: (id: string, input: CreateSubscriptionInput) =>
    api.post<Subscription>(`/api/mgmt/recipients/${encodeURIComponent(id)}/subscriptions`, input),
  updateSub: (id: string, subId: string, patch: UpdateSubscriptionInput) =>
    api.patch<Subscription>(
      `/api/mgmt/recipients/${encodeURIComponent(id)}/subscriptions/${encodeURIComponent(subId)}`,
      patch
    ),
  removeSub: (id: string, subId: string, hard = true) =>
    api.del<void>(
      `/api/mgmt/recipients/${encodeURIComponent(id)}/subscriptions/${encodeURIComponent(subId)}${hard ? '?hard=1' : ''}`
    ),
  testSub: (id: string, subId: string) =>
    api.post<{ ok: boolean }>(
      `/api/mgmt/recipients/${encodeURIComponent(id)}/subscriptions/${encodeURIComponent(subId)}/test`
    )
};
